import { inject, Injectable } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter } from "rxjs/operators";
import { AdobeAnalyticsService } from "./adobe-analytics.service";
import { DataLayerEvent } from "./adobe-analytics.models";

declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
    adobeLaunch?: {
      trackPageLoadEvent(page: Record<string, string>): void;
      trackUserData(environment?: string): Promise<void>;
      trackPageLoadCompleted(): void;
    };
  }
}

@Injectable({ providedIn: "root" })
export class SpaRouteTrackerService {
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AdobeAnalyticsService);
  private initialized = false;
  private isFirstNavigation = true;
  private cachedUserData: Record<string, string> | null = null;

  /**
   * Call once in AppComponent.ngOnInit().
   * Subscribes to Angular Router NavigationEnd events.
   * Skips the first navigation (C3/ADL handles initial page load).
   * On subsequent navigations: delegates to window.adobeLaunch for the full
   * lifecycle sequence: trackPageLoadEvent → trackUserData → trackPageLoadCompleted.
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (this.isFirstNavigation) {
          this.isFirstNavigation = false;
          return;
        }

        this.onRouteChange(event.urlAfterRedirects);
      });
  }

  private async onRouteChange(url: string): Promise<void> {
    const adl = window.adobeLaunch;
    if (!adl) {
      return;
    }

    // Defer reset if a beacon-triggering event was pushed, so Launch can finish reading eVars.
    if (this.analyticsService.consumePendingBeacon()) {
      await this.waitForBeaconProcessing();
    }

    // Capture user data from C3 bootstrap's userDetected push before we reset
    if (!this.cachedUserData) {
      this.captureUserDataFromDataLayer();
    }

    // ETM spec: reset dataLayer before SPA route lifecycle to clear stale events
    if (window.dataLayer) {
      window.dataLayer.length = 0;
    }

    // Pre-push cached user data so Launch data elements can read it when trackPageLoadEvent fires
    if (this.cachedUserData && window.dataLayer) {
      window.dataLayer.push({ event: "userDetected", user: this.cachedUserData });
    }

    const page = this.buildPageData(url);

    adl.trackPageLoadEvent(page);
    await adl.trackUserData();
    adl.trackPageLoadCompleted();
  }

  private captureUserDataFromDataLayer(): void {
    if (!window.dataLayer) return;

    const userEvent = window.dataLayer.find(
      (entry) => entry.event === "userDetected" && entry["user"] && typeof entry["user"] === "object",
    );
    if (!userEvent || !userEvent["user"]) return;

    const user = userEvent["user"] as Record<string, string>;
    // Skip incomplete shells (e.g., F&R returns only loginStatus + crewsPOID)
    if (Object.keys(user).length <= 2) return;

    this.cachedUserData = { ...user };
  }

  private waitForBeaconProcessing(): Promise<void> {
    return new Promise<void>((resolve) => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => resolve(), { timeout: 150 });
      } else {
        setTimeout(resolve, 50);
      }
    });
  }

  private buildPageData(url: string): Record<string, string> {
    const siteIdentifier = this.getSiteIdentifier();
    const hostname = window.location.hostname.split(".")[0];

    let path = url.split("?")[0].replace(/^\//, "");
    let segments = path.split("/").filter(Boolean);

    // Fallback to window.location.pathname when router URL resolves to root "/"
    // (e.g., F&R's root route is "/" internally but deployed at /family-and-relationships/)
    if (segments.length === 0) {
      path = window.location.pathname.replace(/^\//, "");
      segments = path.split("/").filter(Boolean);
    }

    const siteSection1 = segments[0] || "none";
    const siteSection2 = segments[1] || "none";

    return {
      pageName: `us:en:${siteIdentifier}:web:${hostname}:${siteSection1}`,
      pageTitle: this.formatPageTitle(siteSection1),
      siteSection1,
      siteSection2,
    };
  }

  private getSiteIdentifier(): string {
    try {
      const isInternal = !!JSON.parse(sessionStorage.getItem("internal") || "false");
      return isInternal ? "wave" : "retail";
    } catch {
      return "retail";
    }
  }

  private formatPageTitle(section: string): string {
    return section
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
}
